import React from 'react';
import { Link } from '@inertiajs/react';
import { formatDistanceToNow, format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Mail,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Edit,
  Send,
  User,
  Calendar,
  Target,
  MailCheck,
  MailX,
  Loader2,
} from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';

interface Campaign {
  id: number;
  subject: string;
  body_html: string;
  status: 'draft' | 'sending' | 'sent';
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  sent_at: string | null;
  recipient_filter?: {
    roles?: string[];
    categories?: string[];
    match_status?: 'all' | 'unmatched' | 'matched'
  };
  sent_by?: {
    name: string;
    email: string;
  };
}

interface Recipient {
  id: number;
  user_name: string;
  user_email: string;
  sent_at: string | null;
  failed_at: string | null;
  error_message: string | null;
  status: 'pending' | 'sent' | 'failed';
}

interface Props {
  campaign: Campaign;
  recipients: Recipient[];
}

export default function CampaignShow({ campaign, recipients }: Props) {
  const getStatusBadge = (status: Campaign['status']) => {
    const icons = {
      draft: <AlertCircle className="h-3 w-3" />,
      sending: <Loader2 className="h-3 w-3 animate-spin" />,
      sent: <MailCheck className="h-3 w-3" />,
    };

    return (
      <Badge variant={status === 'draft' ? 'secondary' : 'default'} className="flex items-center gap-1">
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getRecipientStatusIcon = (status: Recipient['status']) => {
    switch (status) {
      case 'sent':
        return <div className="h-2 w-2 rounded-full bg-green-500" />;
      case 'failed':
        return <div className="h-2 w-2 rounded-full bg-red-500" />;
      default:
        return <div className="h-2 w-2 rounded-full bg-gray-400" />;
    }
  };

  const formatFilterDescription = () => {
    if (!campaign.recipient_filter) return 'All verified users';

    const parts = [];
    if (campaign.recipient_filter.roles?.length) {
      parts.push(campaign.recipient_filter.roles.map(r => r.charAt(0).toUpperCase() + r.slice(1) + 's').join(' and '));
    }
    if (campaign.recipient_filter.categories?.length) {
      parts.push(campaign.recipient_filter.categories.map(c =>
        c.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-')
      ).join(' and '));
    }
    if (campaign.recipient_filter?.match_status) {
      parts.push(`Match Status: ${campaign.recipient_filter.match_status}`)
    } else {
      parts.push('Match Status: All')
    }
    return parts.join('\n')
  };

  return (
    <AdminLayout title='Campaign Details'>
      <div className="">
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <Link href="/admin/campaigns">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="hover:bg-gray-100"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </Link>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    Campaign Details
                  </h1>
                  <p className="text-gray-600 mt-1">
                    View campaign information and delivery status
                  </p>
                </div>
              </div>
              {campaign.status === 'draft' && (
                <div className="flex gap-3">
                  <Link href={`/admin/campaigns/${campaign.id}/edit`}>
                    <Button variant="outline">
                      <Edit className="mr-2 h-4 w-4" />
                      Edit Campaign
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Campaign Info */}
              <Card className="border-0 shadow-sm pt-0 pb-6">
                <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg font-medium">Campaign Information</CardTitle>
                    {getStatusBadge(campaign.status)}
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Subject Line</p>
                    <p className="text-lg font-medium text-gray-900">{campaign.subject}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Target Audience</p>
                      <p className="text-gray-900 whitespace-pre-line">{formatFilterDescription()}</p>
                    </div>

                    {campaign.sent_by && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Created by</p>
                        <p className="text-gray-900">{campaign.sent_by.name}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Created</p>
                      <p className="text-gray-900">
                        {formatDistanceToNow(new Date(campaign.created_at), { addSuffix: true })}
                      </p>
                    </div>

                    {campaign.sent_at && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Sent</p>
                        <p className="text-gray-900">
                          {formatDistanceToNow(new Date(campaign.sent_at), { addSuffix: true })}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Email Content */}
              <Card className="border-0 shadow-sm pt-0 pb-6">
                <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                  <CardTitle className="text-lg font-medium">Email Content</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="p-6 bg-white">
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: campaign.body_html }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Recipients List (if sent) */}
              {recipients.length > 0 && (
                <Card className="border-0 shadow-sm pt-0 pb-6">
                  <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                    <div>
                      <CardTitle className="text-lg font-medium">Recipients</CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        {recipients.length} {recipients.length === 1 ? 'recipient' : 'recipients'}
                        {recipients.length >= 100 && ' (showing first 100)'}
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-gray-100">
                      {recipients.map((recipient) => (
                        <div
                          key={recipient.id}
                          className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {getRecipientStatusIcon(recipient.status)}
                            <div>
                              <p className="font-medium text-gray-900">{recipient.user_name}</p>
                              <p className="text-sm text-gray-500">{recipient.user_email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {recipient.sent_at && (
                              <p className="text-sm text-green-600">
                                Sent {formatDistanceToNow(new Date(recipient.sent_at), { addSuffix: true })}
                              </p>
                            )}
                            {recipient.failed_at && (
                              <div>
                                <p className="text-sm text-red-600">Failed</p>
                                {recipient.error_message && (
                                  <p className="text-xs text-gray-500">{recipient.error_message}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar - Stats */}
            <div className="space-y-6">
              <Card className="border-0 shadow-sm pt-0 pb-6">
                <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                  <CardTitle className="text-lg font-medium">Delivery Statistics</CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total Recipients</span>
                      <Mail className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-3xl font-light">{campaign.recipient_count}</p>
                    <div className="h-1 w-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full mt-2" />
                  </div>

                  {campaign.status !== 'draft' && (
                    <>
                      <div className="pt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Delivered</span>
                          <MailCheck className="h-4 w-4 text-green-500" />
                        </div>
                        <p className="text-3xl font-light text-green-600">{campaign.sent_count}</p>
                        <div className="h-1 w-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full mt-2" />
                      </div>

                      {campaign.failed_count > 0 && (
                        <div className="pt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Failed</span>
                            <MailX className="h-4 w-4 text-red-500" />
                          </div>
                          <p className="text-3xl font-light text-red-600">{campaign.failed_count}</p>
                          <div className="h-1 w-12 bg-gradient-to-r from-red-500 to-red-600 rounded-full mt-2" />
                        </div>
                      )}

                      {campaign.sent_count > 0 && (
                        <div className="pt-6 border-t border-gray-100">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Success Rate</span>
                            <span className="text-lg font-medium text-gray-900">
                              {Math.round((campaign.sent_count / campaign.recipient_count) * 100)}%
                            </span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-500"
                              style={{
                                width: `${(campaign.sent_count / campaign.recipient_count) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
